import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { ENABLE_DEVTOOLS } from '../config/env'
import { PAGE_PATHS } from '../config/pagePaths'
import { useAuthentication } from '../hooks/useAuthentication'
import { useAuthWatcher } from '../hooks/useAuthWatcher'
import { selectCompany } from '../store/company'
import { selectUser } from '../store/user'
import { color } from '../styles'
import { device } from '../styles/mixins/device'
import { useLaptopMediaQuery } from '../styles/mixins/mediaQueryHooks'
import { text } from '../styles/mixins/text'
import { opacity } from '../util/colorTools'
import { isEqual } from '../util/isEqual'

import { Layout } from './Layout'
import { Navbar } from './Navbar'
import { NavbarMobile } from './NavbarMobile'
import { Spacer } from './Spacer'
import { Spinner } from './Spinner'

type Props = {
  children: JSX.Element | JSX.Element[]
  className?: string
}

const LoadingTakeover = (): JSX.Element => {
  return (
    <TakeoverBody>
      <LoadingContainer>
        <Spinner size={11} />
        <Spacer vertical size={6} />
        Loading...
      </LoadingContainer>
    </TakeoverBody>
  )
}

export const AuthLayout = ({ children, className }: Props): JSX.Element => {
  const isLaptop = useLaptopMediaQuery()
  const { isInit, isLoading, logout } = useAuthentication()
  const user = useSelector(selectUser, isEqual)
  const company = useSelector(selectCompany, isEqual)
  const navigate = useNavigate()
  const [devtoolVisible, setDevtoolVisible] = useState(false)

  useAuthWatcher({
    onChange: (nextUser) => {
      if (!nextUser) navigate(PAGE_PATHS.auth)
    },
  })

  const handleDevtoolToggle = (): void => {
    if (!ENABLE_DEVTOOLS) return
    setDevtoolVisible(!devtoolVisible)
  }

  const envNotReady = !isInit || isLoading

  return (
    <Layout className={className}>
      {envNotReady ? <LoadingTakeover /> : <div />}
      <Container>
        {!isLaptop ? (
          <>
            <MobileNavContainer>
              <NavbarMobile
                company={isLoading ? null : company}
                onLogout={() => {
                  logout()
                }}
              />
            </MobileNavContainer>
            <Spacer size={13} vertical />
          </>
        ) : null}
        {isLaptop ? (
          <Navbar
            user={isLoading ? null : user}
            company={isLoading ? null : company}
            onDevtoolToggle={handleDevtoolToggle}
            devtoolsEnabled={ENABLE_DEVTOOLS}
            onLogout={() => {
              logout()
            }}
          />
        ) : null}
        {children}
      </Container>
    </Layout>
  )
}

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0px;
  min-height: 0px;
  ${device.laptop`
    flex-direction: row;
  `}
`
const MobileNavContainer = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
`

const LoadingContainer = styled.div`
  ${text('header')}
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
`

const TakeoverBody = styled.div`
  background-color: ${(p) => opacity(color.background.content(p), 0.9)};
  height: 100vh;
  width: 100vw;
  position: fixed;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`
